import { type KeyboardEvent, type ReactNode, useRef } from "react";
import {
  Box,
  ChakraModal,
  ChakraModalContent,
  ChakraModalOverlay,
  CloseIcon,
  IconButton,
  popoverVariants,
} from "@hightouch/htui";

import { MediaDetailControlsProvider } from "src/components/media-detail/media-detail-controls";
import type { MediaDetailNavigation } from "src/components/media-detail/use-media-detail-controls";

// Arrow keys belong to the control that has focus when it can use them: text
// entry, and the menus and listboxes the tag chips open.
const ARROW_KEY_OWNERS =
  'input, textarea, select, [contenteditable="true"], [role="menu"], [role="listbox"], [role="slider"]';

type Props = {
  onClose: () => void;
  navigation: MediaDetailNavigation;
  isOpen: boolean;
  "aria-label": string;
  children: ReactNode;
};

/**
 * Full-page dialog for inspecting one creative or asset: the media on a canvas
 * at left, details in a fixed-width panel at right (see `MediaDetailLayout`).
 * Left and right arrow keys step through the source list when `navigation` has
 * one.
 */
export function MediaDetailDialog({
  isOpen,
  onClose,
  navigation,
  "aria-label": ariaLabel,
  children,
}: Props) {
  const contentRef = useRef<HTMLElement>(null);
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (navigation.kind === "none" || event.defaultPrevented) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest(ARROW_KEY_OWNERS)
    ) {
      return;
    }
    const step =
      event.key === "ArrowLeft" ? navigation.previous : navigation.next;
    if (step.kind === "end") return;
    event.preventDefault();
    step.go();
  };

  return (
    // Focus lands on the dialog itself, not its first button: the Previous
    // button's tooltip opens on focus and would show on every open.
    <ChakraModal isOpen={isOpen} onClose={onClose} initialFocusRef={contentRef}>
      {/* ChakraModal defaults to the `popover` layer (1500). Menus, the tag
          pickers and the action chat overlay float on `popover`, so the dialog
          drops to `modal` (1400) to keep them above it. */}
      <ChakraModalOverlay zIndex="modal" />
      {/* Sized in `100%`, not `100vw`: `100vw` includes the scrollbar gutter
          that the fixed-position containing block excludes. The content is a
          sibling of the overlay, so it needs explicit enter/exit labels. */}
      <ChakraModalContent
        ref={contentRef}
        aria-label={ariaLabel}
        containerProps={{ zIndex: "modal", width: "100%" }}
        w="100%"
        h="100vh"
        maxW="100%"
        m={0}
        py={0}
        borderRadius={0}
        overflow="hidden"
        bg="base.lightBackground"
        motionProps={{ ...popoverVariants, animate: "enter", exit: "exit" }}
        onKeyDown={handleKeyDown}
      >
        <Box position="absolute" top={4} left={4} zIndex={1}>
          <IconButton
            aria-label="Close"
            icon={CloseIcon}
            variant="secondary"
            shape="circle"
            onClick={onClose}
          />
        </Box>
        {/* The close button lives in the dialog corner, so the header only
            gets prev/next. */}
        <MediaDetailControlsProvider
          navigation={navigation}
          close={{ kind: "none" }}
        >
          {children}
        </MediaDetailControlsProvider>
      </ChakraModalContent>
    </ChakraModal>
  );
}
